import { t } from '../i18n/index.ts';

/**
 * A country pack as the screens get it (packages/core/src/countries.ts): what the business numbers
 * are called and how they look, the tax, the money, the paper, the address and the notes the law
 * asks for. Forms read their labels and checks from here, so no screen names one country's number.
 */
export interface IdRule {
  label: string;
  example: string | null;
  /** The pattern as text; null where the format is not certain (kept as typed). */
  pattern: string | null;
  rule: string | null;
}

export interface Region {
  code: string;
  name: string;
  /** The sales tax a new location there starts with (Canada). */
  rate?: number;
}

export interface CountryRules {
  code: string;
  name: string;
  group: 'eu' | 'europe' | 'north_america' | 'other';
  currency: string;
  tax: 'vat' | 'sales_tax';
  taxName: string;
  prices: 'with_tax' | 'without_tax';
  taxId: IdRule;
  vatId: IdRule | null;
  regNo: IdRule | null;
  paper: 'A4' | 'Letter';
  timezone: string;
  formats: { locale: string; firstDay: 0 | 1 };
  address: { postcode: IdRule | null; regionLabel: string | null; order: 'postcode_city' | 'city_postcode' | 'city_region_postcode' | 'postcode_city_region' };
  regions: Region[] | null;
  fiscalYearStart: string;
  chart: string;
  mentions: { reverseCharge: string | null; smallBusiness: string | null; latePayment: string | null };
  /** In the small business scheme: no VAT charged, the note goes on every invoice. */
  smallBusinessScheme: boolean;
  fiscal: { system: string; status: 'ready' | 'needs_spec' | 'none' };
  einvoice: { formats: string[]; note: string | null; idLabel: string | null };
}

/** One line of the country list: the countries with a ready pack come first. */
export interface PackSummary {
  code: string;
  name: string;
  group: CountryRules['group'];
  currency: string;
  tax: CountryRules['tax'];
  fiscal: CountryRules['fiscal']['status'];
}

/** A business number the way the server keeps it: capitals, no spaces. */
export const cleanId = (s: string) => s.toUpperCase().replace(/\s/g, '');

const test = (pattern: string, value: string) => {
  try {
    return new RegExp(pattern).test(value);
  } catch {
    return true;
  }
};

/** What is wrong with a number for its rule, or null; an empty one is never wrong here. */
export function idError(rule: IdRule | null | undefined, value: string, opts: { keepSpaces?: boolean } = {}): string | null {
  const v = opts.keepSpaces ? value.trim().toUpperCase() : cleanId(value);
  if (!v || !rule?.pattern) return null;
  return test(rule.pattern, v) ? null : (rule.rule ?? t('country.id_rule', { name: rule.label }));
}

/** Whether a number matches a rule that is certain: shown as a quiet "looks right". */
export const idLooksRight = (rule: IdRule | null | undefined, value: string, opts: { keepSpaces?: boolean } = {}) =>
  !!rule?.pattern && !!value.trim() && !idError(rule, value, opts);

/** The hint under a number's field: its example, or a general line. */
export const idHint = (rule: IdRule | null | undefined) => (rule?.example ? t('business.tax_id_example', { example: rule.example }) : t('business.tax_id_hint_other'));

/** One address line for the city, the postcode and the state, the way the country writes it. */
export function cityLine(rules: Pick<CountryRules, 'address'> | null | undefined, a: { city?: string | null; postcode?: string | null; region?: string | null }): string {
  const city = a.city?.trim() ?? '';
  const pc = a.postcode?.trim() ?? '';
  const region = a.region?.trim() ?? '';
  const join = (...xs: string[]) => xs.filter(Boolean).join(' ');
  switch (rules?.address.order) {
    case 'city_region_postcode':
      return join(city && region ? `${city},` : city, region, pc);
    case 'city_postcode':
      return join(city, pc);
    case 'postcode_city_region':
      return join(pc, city, region);
    default:
      return join(pc, city);
  }
}
